import {
  Badge,
  Body1,
  Button,
  Dropdown,
  Field,
  Input,
  Option,
  Spinner,
  Text,
  Textarea,
  Title3,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { Department, Kiosk, KioskEnrollment } from '@worklogix/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SHRINKABLE } from '../responsive';

import { useTimeAdminApi } from './api';
import { DataTable } from './components/DataTable';
import type { DataColumn } from './components/DataTable';
import { SideBlade } from './components/SideBlade';
import { toErrorMessage } from './errors';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    // Every ancestor between the page and a table's scroller must be able to
    // shrink, or the overflow lands on the document instead. See responsive.ts.
    ...SHRINKABLE,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    // Title + action buttons cannot share one line on a phone. Without wrapping,
    // this row is wider than the screen and — being a non-scrolling child — it
    // drags the whole content region sideways, clipping everything's left edge
    // (Michael, 2026-09-10: "Employees" rendered as "ees").
    flexWrap: 'wrap',
    ...SHRINKABLE,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
    flexWrap: 'wrap',
  },
  tokenPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    border: `${tokens.strokeWidthThick} solid ${tokens.colorPaletteYellowBorder1}`,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  tokenText: {
    fontFamily: tokens.fontFamilyMonospace,
    wordBreak: 'break-all',
  },
  warn: {
    color: tokens.colorPaletteDarkOrangeForeground1,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
  },
});

/**
 * One-time device-token panel. The minted token is shown exactly ONCE (it is
 * never retrievable again); the panel offers a copy affordance and a clear
 * warning. Rendered after a register or rotate and cleared when the blade closes.
 */
function TokenPanel({ token }: { token: string }): JSX.Element {
  const styles = useStyles();
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    void navigator.clipboard
      .writeText(token)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  }, [token]);

  return (
    <div className={styles.tokenPanel} role="status">
      <Text className={styles.warn} weight="semibold">
        Copy this device token now — it is shown only once and cannot be retrieved again.
      </Text>
      <Textarea value={token} textarea={{ className: styles.tokenText, rows: 3, readOnly: true }} />
      <div className={styles.actions}>
        <Button appearance="primary" onClick={copy}>
          {copied ? 'Copied' : 'Copy token'}
        </Button>
      </div>
    </div>
  );
}

/**
 * Kiosks tab — register a shared department tablet, view registered kiosks, and
 * rotate or revoke a kiosk's device token. Registering (or rotating) mints a
 * device token that is surfaced exactly once. The device-token HASH is never
 * exposed by the API; only the one-time plaintext on mint.
 */
export function KiosksTab(): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();

  const [kiosks, setKiosks] = useState<Kiosk[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Register blade.
  const [regOpen, setRegOpen] = useState(false);
  const [name, setName] = useState('');
  const [deptId, setDeptId] = useState('');
  const [regSaving, setRegSaving] = useState(false);
  const [regError, setRegError] = useState<string | null>(null);

  // Manage blade.
  const [manage, setManage] = useState<Kiosk | null>(null);
  const [busy, setBusy] = useState(false);
  const [manageError, setManageError] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  // Delete is irreversible, so it takes a second click like revoke does.
  const [confirmDelete, setConfirmDelete] = useState(false);

  // A minted token is shared across whichever blade minted it; shown once.
  const [minted, setMinted] = useState<string | null>(null);
  // Rename (Michael, 2026-09-10): a kiosk registered with a typo could not be
  // corrected from the UI, only deleted — and a kiosk that has recorded time
  // CANNOT be deleted (409), so a misspelling was permanent. Purely cosmetic:
  // the device token, the department binding and recorded punches are untouched.
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renamed, setRenamed] = useState(false);

  const departmentsById = useMemo(() => {
    const m = new Map<string, Department>();
    for (const d of departments) {
      m.set(d.id, d);
    }
    return m;
  }, [departments]);

  const deptLabel = useCallback(
    (id: string): string => departmentsById.get(id)?.name ?? id,
    [departmentsById]
  );

  const reload = useCallback(() => {
    let active = true;
    setLoading(true);
    setError(null);
    void Promise.all([api.listKiosks(), api.listDepartments()])
      .then(([ks, ds]) => {
        if (active) {
          setKiosks(ks);
          setDepartments(ds);
        }
      })
      .catch((err: unknown) => {
        if (active) {
          setError(toErrorMessage(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => reload(), [reload]);

  const openRegister = useCallback(() => {
    setRegOpen(true);
    setName('');
    setDeptId('');
    setRegError(null);
    setMinted(null);
  }, []);

  const closeRegister = useCallback(() => {
    setRegOpen(false);
    setName('');
    setDeptId('');
    setRegSaving(false);
    setRegError(null);
    setMinted(null); // Drop the one-time token from memory.
    reload();
  }, [reload]);

  const applyEnrollment = useCallback((enrollment: KioskEnrollment) => {
    setMinted(enrollment.deviceToken);
  }, []);

  const handleRegister = useCallback(async () => {
    if (name.trim() === '' || deptId === '') {
      return;
    }
    setRegSaving(true);
    setRegError(null);
    try {
      const enrollment = await api.registerKiosk({ name: name.trim(), departmentId: deptId });
      applyEnrollment(enrollment);
    } catch (err: unknown) {
      setRegError(toErrorMessage(err));
    } finally {
      setRegSaving(false);
    }
  }, [api, name, deptId, applyEnrollment]);

  const openManage = useCallback((k: Kiosk) => {
    setManage(k);
    setManageError(null);
    setConfirmRevoke(false);
    setConfirmDelete(false);
    setMinted(null);
    setRenameValue(k.name);
    setRenamed(false);
  }, []);

  const closeManage = useCallback(() => {
    setManage(null);
    setManageError(null);
    setConfirmRevoke(false);
    setConfirmDelete(false);
    setBusy(false);
    setMinted(null); // Drop any one-time token.
    setRenameValue('');
    setRenaming(false);
    setRenamed(false);
    reload();
  }, [reload]);

  const handleRename = useCallback(async () => {
    if (manage === null) {
      return;
    }
    const next = renameValue.trim();
    // Same guards the API applies (non-empty, <=200) so the button cannot post a
    // body the server will reject.
    if (next === '' || next === manage.name || next.length > 200) {
      return;
    }
    setRenaming(true);
    setManageError(null);
    setRenamed(false);
    try {
      const updated = await api.renameKiosk(manage.id, next);
      // Update the open blade AND the row behind it, so the list does not keep
      // showing the old name until the tab is reloaded.
      setManage(updated);
      setKiosks((prev) => prev.map((k) => (k.id === updated.id ? updated : k)));
      setRenameValue(updated.name);
      setRenamed(true);
    } catch (err: unknown) {
      setManageError(toErrorMessage(err));
    } finally {
      setRenaming(false);
    }
  }, [api, manage, renameValue]);

  const handleRotate = useCallback(async () => {
    if (manage === null) {
      return;
    }
    setBusy(true);
    setManageError(null);
    try {
      const enrollment = await api.rotateKioskToken(manage.id);
      applyEnrollment(enrollment);
    } catch (err: unknown) {
      setManageError(toErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }, [api, manage, applyEnrollment]);

  const handleRevoke = useCallback(async () => {
    if (manage === null) {
      return;
    }
    setBusy(true);
    setManageError(null);
    try {
      await api.revokeKiosk(manage.id);
      closeManage();
    } catch (err: unknown) {
      setManageError(toErrorMessage(err));
      setBusy(false);
    }
  }, [api, manage, closeManage]);

  const handleReactivate = useCallback(async () => {
    if (manage === null) {
      return;
    }
    setBusy(true);
    setManageError(null);
    try {
      await api.reactivateKiosk(manage.id);
      closeManage();
    } catch (err: unknown) {
      setManageError(toErrorMessage(err));
      setBusy(false);
    }
  }, [api, manage, closeManage]);

  /**
   * Delete the kiosk for good. The server REFUSES any kiosk that has recorded
   * time (409) — that history is payroll evidence — so the error is surfaced in
   * the blade rather than pre-judged here, and it names deactivating instead.
   */
  const handleDelete = useCallback(async () => {
    if (manage === null) {
      return;
    }
    setBusy(true);
    setManageError(null);
    try {
      await api.deleteKiosk(manage.id);
      closeManage();
    } catch (err: unknown) {
      setManageError(toErrorMessage(err));
      setConfirmDelete(false);
      setBusy(false);
    }
  }, [api, manage, closeManage]);

  const columns: ReadonlyArray<DataColumn<Kiosk>> = useMemo(
    () => [
      { columnId: 'name', header: 'Kiosk', renderCell: (k) => k.name },
      { columnId: 'dept', header: 'Department', renderCell: (k) => deptLabel(k.departmentId) },
      {
        columnId: 'status',
        header: 'Status',
        renderCell: (k) => (
          <Badge appearance="tint" color={k.active ? 'success' : 'subtle'}>
            {k.active ? 'Active' : 'Revoked'}
          </Badge>
        ),
      },
    ],
    [deptLabel]
  );

  const canRegister = name.trim().length > 0 && deptId !== '' && !regSaving;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Title3>Kiosks</Title3>
        <Button appearance="primary" onClick={openRegister}>
          Register kiosk
        </Button>
      </div>

      {error !== null ? (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      ) : null}

      {loading ? (
        <Spinner label="Loading kiosks…" />
      ) : (
        <DataTable
          columns={columns}
          rows={kiosks}
          getRowId={(k) => k.id}
          onRowClick={openManage}
          emptyMessage="No kiosks registered yet."
        />
      )}

      {/* Register blade */}
      <SideBlade open={regOpen} onClose={closeRegister} title="Register kiosk">
        {minted !== null ? (
          <div className={styles.form}>
            <Text>Kiosk registered. Pair the tablet with the token below.</Text>
            <TokenPanel token={minted} />
            <div className={styles.actions}>
              <Button appearance="primary" onClick={closeRegister}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form
            className={styles.form}
            onSubmit={(ev) => {
              ev.preventDefault();
              if (canRegister) {
                void handleRegister();
              }
            }}
          >
            <Field label="Kiosk name" required>
              <Input
                value={name}
                onChange={(_, d) => setName(d.value)}
                disabled={regSaving}
                placeholder="Blend Line 2 Tablet"
              />
            </Field>
            <Field label="Department" required hint="The device is bound to this department.">
              <Dropdown
                value={deptId === '' ? '' : deptLabel(deptId)}
                selectedOptions={deptId === '' ? [] : [deptId]}
                placeholder="Select a department"
                disabled={regSaving || departments.length === 0}
                onOptionSelect={(_, d) => setDeptId(d.optionValue ?? '')}
              >
                {departments.map((d) => (
                  <Option key={d.id} value={d.id} text={d.name}>
                    {d.name}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            {regError !== null ? (
              <Text className={styles.error} role="alert">
                {regError}
              </Text>
            ) : null}
            <div className={styles.actions}>
              <Button appearance="secondary" onClick={closeRegister} disabled={regSaving}>
                Cancel
              </Button>
              <Button appearance="primary" type="submit" disabled={!canRegister}>
                {regSaving ? <Spinner size="tiny" /> : 'Register'}
              </Button>
            </div>
          </form>
        )}
      </SideBlade>

      {/* Manage blade */}
      <SideBlade
        open={manage !== null}
        onClose={closeManage}
        title={manage === null ? '' : `Manage — ${manage.name}`}
      >
        {manage !== null ? (
          <div className={styles.form}>
            <Body1>
              {deptLabel(manage.departmentId)} · {manage.active ? 'Active' : 'Revoked'}
            </Body1>

            {/*
              Rename sits ABOVE the token/revoke/delete actions on purpose: it is the
              only harmless thing on this blade, and it is what an admin comes here
              to do after a typo. Enabled for a revoked kiosk too — a misspelled name
              is just as wrong once the device is retired, and correcting it changes
              nothing operational.
            */}
            <Field
              label="Kiosk name"
              hint="Display name only — the device token, its department and any recorded time are unaffected."
            >
              <Input
                value={renameValue}
                onChange={(_, d) => {
                  setRenameValue(d.value);
                  setRenamed(false);
                }}
                disabled={renaming}
                maxLength={200}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') {
                    ev.preventDefault();
                    void handleRename();
                  }
                }}
              />
            </Field>
            <div className={styles.actions}>
              <Button
                appearance="secondary"
                onClick={() => void handleRename()}
                disabled={
                  renaming ||
                  renameValue.trim() === '' ||
                  renameValue.trim() === manage.name
                }
              >
                {renaming ? <Spinner size="tiny" /> : 'Save name'}
              </Button>
              {renamed ? <Text>Name updated.</Text> : null}
            </div>

            {minted !== null ? (
              <>
                <Text>Token rotated. The previous token is now invalid.</Text>
                <TokenPanel token={minted} />
              </>
            ) : (
              <>
                <Text>
                  Rotating mints a new device token and immediately invalidates the current one.
                  {manage.active
                    ? ' Revoking deactivates the kiosk entirely; deleting removes it, and is only possible while it has recorded no time.'
                    : ' This kiosk is revoked. Reactivating re-enables it; rotate a new token if the device was wiped.'}
                </Text>
                {manageError !== null ? (
                  <Text className={styles.error} role="alert">
                    {manageError}
                  </Text>
                ) : null}
                <div className={styles.actions}>
                  <Button appearance="primary" onClick={() => void handleRotate()} disabled={busy}>
                    {busy ? <Spinner size="tiny" /> : 'Rotate token'}
                  </Button>
                  {manage.active ? (
                    confirmRevoke ? (
                      <Button
                        appearance="secondary"
                        onClick={() => void handleRevoke()}
                        disabled={busy}
                      >
                        Confirm revoke
                      </Button>
                    ) : (
                      <Button
                        appearance="secondary"
                        onClick={() => setConfirmRevoke(true)}
                        disabled={busy}
                      >
                        Revoke kiosk
                      </Button>
                    )
                  ) : (
                    <Button
                      appearance="secondary"
                      onClick={() => void handleReactivate()}
                      disabled={busy}
                    >
                      Reactivate kiosk
                    </Button>
                  )}
                  {/*
                    Delete is for the TEST kiosks left over from setting the estate
                    up. Two clicks, and the server still refuses any kiosk that has
                    recorded time — so the confirm is about intent, not safety.
                  */}
                  {confirmDelete ? (
                    <Button
                      appearance="secondary"
                      onClick={() => void handleDelete()}
                      disabled={busy}
                    >
                      Confirm delete
                    </Button>
                  ) : (
                    <Button
                      appearance="subtle"
                      onClick={() => setConfirmDelete(true)}
                      disabled={busy}
                    >
                      Delete kiosk
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        ) : null}
      </SideBlade>
    </div>
  );
}
